import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useState } from "react";
import {
  Paper,
  Title,
  Text,
  Textarea,
  Button,
  Stack,
  Group,
  Image,
  Box,
  Anchor,
} from "@mantine/core";
import { PageLoader } from "@/components/common";
import { apartmentsApi, messagesApi } from "@/api/client";

export const Route = createFileRoute("/_authenticated/messages/compose/$id")({
  component: ComposeMessagePage,
});

function ComposeMessagePage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();

  const { data: apartment, isLoading } = useQuery({
    queryKey: ["apartments", id],
    queryFn: () => apartmentsApi.get(Number(id)),
  });

  const defaultMessage = apartment
    ? `Hi${apartment.landlord_name ? ` ${apartment.landlord_name}` : ""}!

I am interested in your apartment${apartment.address ? ` at ${apartment.address}` : ""}.

I would like to schedule a viewing.

Best regards`
    : "";

  const [content, setContent] = useState("");

  useState(() => {
    if (apartment && !content) {
      setContent(defaultMessage);
    }
  });

  const sendMutation = useMutation({
    mutationFn: (content: string) => messagesApi.create(Number(id), content),
    onSuccess: () => {
      navigate({ to: "/apartments/$id", params: { id } });
    },
  });

  if (isLoading || !apartment) {
    return <PageLoader />;
  }

  return (
    <Box maw={700} mx="auto">
      <Stack gap="md">
        <Anchor component={Link} to="/apartments/$id" params={{ id }} c="primary">
          &larr; Back to apartment
        </Anchor>

        <Title order={1}>Compose Message</Title>

        <Paper p="md" radius="md" withBorder style={{ borderColor: "#0f3460" }}>
          <Group>
            {apartment.images?.[0] && (
              <Image
                src={apartment.images[0]}
                alt={apartment.title}
                w={96}
                h={96}
                radius="md"
                fit="cover"
              />
            )}
            <Stack gap={4}>
              <Text size="lg" fw={600}>
                {apartment.title}
              </Text>
              <Text c="dimmed">{apartment.address}</Text>
              <Text c="primary" fw={700}>
                {apartment.rent.toLocaleString()} kr/mo
              </Text>
            </Stack>
          </Group>
        </Paper>

        <Paper p="md" radius="md" withBorder style={{ borderColor: "#0f3460" }}>
          <Stack gap="md">
            <Title order={3}>Message to Landlord</Title>
            <Textarea
              value={content || defaultMessage}
              onChange={(e) => setContent(e.currentTarget.value)}
              minRows={8}
            />
            <Text size="sm" c="dimmed">
              This message will be saved as a draft. You can send it manually on Qasa.
            </Text>
            <Group>
              <Button
                onClick={() => sendMutation.mutate(content || defaultMessage)}
                loading={sendMutation.isPending}
              >
                Save Draft
              </Button>
              <Button
                variant="filled"
                color="#0f3460"
                component={Link}
                to="/apartments/$id"
                params={{ id }}
              >
                Cancel
              </Button>
            </Group>
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
}
